import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { User, Settings, LogOut, LayoutDashboard, FolderKanban, Menu, X, Loader2 } from 'lucide-react';
import Login from './Login';
import Register from './Register';
import Dashboard from './Dashboard';
import ProjectList from './ProjectList';
import ProjectDetail from './ProjectDetail';
import Profile from './Profile';
import SettingsPage from './Settings';
import CreateProjectWizard from './CreateProjectWizard';
import apiService from './apiService';
import './AppUnderTest.css';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Layout component for authenticated pages
const AuthenticatedLayout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/projects', label: 'Projects', icon: FolderKanban },
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="aut-layout">
      {/* Mobile menu button */}
      <button
        className="aut-mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`aut-sidebar ${sidebarOpen ? 'aut-sidebar-open' : ''}`}>
        <div className="aut-sidebar-header">
          <h1 className="aut-logo">TestApp</h1>
        </div>

        <nav className="aut-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`aut-nav-item ${isActive(item.path) ? 'aut-nav-item-active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="aut-sidebar-footer">
          <div className="aut-user-info">
            <div className="aut-avatar">
              {user.name.charAt(0)}
            </div>
            <div className="aut-user-details">
              <span className="aut-user-name">{user.name}</span>
              <span className="aut-user-email">{user.email}</span>
            </div>
          </div>
          <button
            className="aut-logout-btn"
            onClick={onLogout}
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="aut-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="aut-main">
        {children}
      </main>
    </div>
  );
};

// Loading spinner component
const LoadingScreen = () => (
  <div className="aut-loading-screen">
    <Loader2 className="aut-loading-spinner" size={48} />
    <p>Loading...</p>
  </div>
);

// Main App Under Test component
const AppUnderTest = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = apiService.getToken();
      if (token) {
        try {
          const { user } = await apiService.getCurrentUser();
          setUser(user);
          setIsAuthenticated(true);
        } catch (error) {
          console.log('[Auth] Token invalid or expired, clearing...');
          apiService.removeToken();
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const handleLogin = async (email, password) => {
    const result = await apiService.login(email, password);
    setUser(result.user);
    setIsAuthenticated(true);
    return result;
  };

  const handleRegister = async (userData) => {
    const result = await apiService.register(userData);
    setUser(result.user);
    setIsAuthenticated(true);
    return result;
  };

  const handleLogout = async () => {
    await apiService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Show loading screen while checking auth
  if (isLoading) {
    return (
      <div className="aut-container">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, handleLogin, handleRegister, handleLogout, updateUser }}>
      <div className="aut-container">
        <Routes>
          {/* Public routes */}
          <Route
            path="login"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" replace /> :
                <Login />
            }
          />
          <Route
            path="register"
            element={
              isAuthenticated ?
                <Navigate to="/dashboard" replace /> :
                <Register />
            }
          />

          {/* Protected routes */}
          <Route
            path="dashboard"
            element={
              isAuthenticated ?
                <AuthenticatedLayout user={user} onLogout={handleLogout}>
                  <Dashboard />
                </AuthenticatedLayout> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="projects"
            element={
              isAuthenticated ?
                <AuthenticatedLayout user={user} onLogout={handleLogout}>
                  <ProjectList />
                </AuthenticatedLayout> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="projects/:id"
            element={
              isAuthenticated ?
                <AuthenticatedLayout user={user} onLogout={handleLogout}>
                  <ProjectDetail />
                </AuthenticatedLayout> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="profile"
            element={
              isAuthenticated ?
                <AuthenticatedLayout user={user} onLogout={handleLogout}>
                  <Profile />
                </AuthenticatedLayout> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="settings"
            element={
              isAuthenticated ?
                <AuthenticatedLayout user={user} onLogout={handleLogout}>
                  <SettingsPage />
                </AuthenticatedLayout> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="create-project"
            element={
              isAuthenticated ?
                <AuthenticatedLayout user={user} onLogout={handleLogout}>
                  <CreateProjectWizard />
                </AuthenticatedLayout> :
                <Navigate to="/login" replace />
            }
          />

          {/* Default redirect */}
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
          />
        </Routes>
      </div>
    </AuthContext.Provider>
  );
};

export default AppUnderTest;
